import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import YhInput from "./YhInput";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const pad2 = (n: number) => String(n).padStart(2, "0");

export const toDateKey = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export const parseDateKey = (key?: string | null): Date | null => {
  if (!key) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

/** DD-MM-YYYY <-> Date helpers for lead filters */
export const formatDdMmYyyy = (d: Date) =>
  `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;

export const parseDdMmYyyy = (value?: string | null): Date | null => {
  if (!value) return null;
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value.trim());
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDisplayDate = (d: Date) =>
  `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;

export const formatDisplayDateTime = (d: Date) => {
  let h = d.getHours();
  const m = d.getMinutes();
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${formatDisplayDate(d)}, ${pad2(h)}:${pad2(m)} ${period}`;
};

/** YYYY-MM-DDTHH:mm as a local wall clock. Avoids Date.parse treating the string as UTC. */
export const parseLocalDateTime = (value?: string | null): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(String(value || "").trim());
  if (!m) return null;
  const d = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    0,
    0
  );
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Mongo ObjectId embeds the real insert time. Note createdAt was frozen at server start. */
export const instantFromObjectId = (id?: string | null): Date | null => {
  const hex = String(id || "");
  if (!/^[a-f0-9]{24}$/i.test(hex)) return null;
  const seconds = parseInt(hex.slice(0, 8), 16);
  if (!Number.isFinite(seconds) || seconds < 1_000_000_000) return null;
  return new Date(seconds * 1000);
};

export const formatIstDateTime = (d: Date) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(d);

export const formatRecordedAt = (item: any) => {
  const fromId = instantFromObjectId(item?._id);
  if (fromId) return formatIstDateTime(fromId);
  const raw = item?.createdAt || item?.updatedAt;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return formatIstDateTime(d);
  }
  return [item?.date, item?.time].filter(Boolean).join(" ");
};

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

type CalendarGridProps = {
  view: Date;
  onViewChange: (d: Date) => void;
  selected?: Date | null;
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  onSelectDay: (d: Date) => void;
  showFooter?: boolean;
  onClear?: () => void;
  onToday?: () => void;
};

export const YhCalendarGrid: React.FC<CalendarGridProps> = ({
  view,
  onViewChange,
  selected,
  rangeStart,
  rangeEnd,
  onSelectDay,
  showFooter = true,
  onClear,
  onToday,
}) => {
  const year = view.getFullYear();
  const month = view.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: Array<Date | null> = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(year, month, day));
  }

  const isSameDay = (a?: Date | null, b?: Date | null) =>
    !!a &&
    !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const inRange = (d: Date) => {
    if (!rangeStart || !rangeEnd) return false;
    const t = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const s = new Date(
      rangeStart.getFullYear(),
      rangeStart.getMonth(),
      rangeStart.getDate()
    ).getTime();
    const e = new Date(
      rangeEnd.getFullYear(),
      rangeEnd.getMonth(),
      rangeEnd.getDate()
    ).getTime();
    return t >= Math.min(s, e) && t <= Math.max(s, e);
  };

  return (
    <div className="w-[260px] select-none p-3">
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          className="flex items-center gap-1 text-sm font-semibold text-[#1E1B4B]"
          onClick={() => onViewChange(new Date(year, month - 1, 1))}
        >
          {MONTHS[month]} {year}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous month"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
            onClick={() => onViewChange(new Date(year, month - 1, 1))}
          >
            ▲
          </button>
          <button
            type="button"
            aria-label="Next month"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
            onClick={() => onViewChange(new Date(year, month + 1, 1))}
          >
            ▼
          </button>
        </div>
      </div>

      <div className="mb-1 grid grid-cols-7 gap-0.5">
        {WEEKDAYS.map((d, i) => (
          <div
            key={`${d}-${i}`}
            className="py-1 text-center text-[11px] font-semibold text-[#9CA3AF]"
          >
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} className="h-8" />;
          const isSelected = isSameDay(d, selected);
          const isStart = isSameDay(d, rangeStart);
          const isEnd = isSameDay(d, rangeEnd);
          const ranged = inRange(d);
          const active = isSelected || isStart || isEnd;
          return (
            <button
              key={toDateKey(d)}
              type="button"
              onClick={() => onSelectDay(d)}
              className={`h-8 rounded-lg text-sm transition ${
                active
                  ? "bg-[#A02448] font-semibold text-white"
                  : ranged
                    ? "bg-[#F9F0F4] font-medium text-[#4338CA]"
                    : "text-[#111827] hover:bg-[#F3F4F6]"
              }`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      {showFooter ? (
        <div className="mt-3 flex items-center justify-between border-t border-[#EEF0F5] pt-2">
          <button
            type="button"
            className="text-sm font-semibold text-[#A02448] hover:underline"
            onClick={onClear}
          >
            Clear
          </button>
          <button
            type="button"
            className="text-sm font-semibold text-[#A02448] hover:underline"
            onClick={onToday}
          >
            Today
          </button>
        </div>
      ) : null}
    </div>
  );
};

type TimeColumnProps = {
  items: string[];
  value: string;
  onChange: (v: string) => void;
};

const TimeColumn: React.FC<TimeColumnProps> = ({ items, value, onChange }) => (
  <div className="max-h-[220px] w-14 overflow-y-auto [scrollbar-width:thin]">
    {items.map((item) => (
      <button
        key={item}
        type="button"
        onClick={() => onChange(item)}
        className={`flex h-8 w-full items-center justify-center rounded-md text-sm transition ${
          item === value
            ? "bg-[#A02448] font-semibold text-white"
            : "text-[#374151] hover:bg-[#F3F4F6]"
        }`}
      >
        {item}
      </button>
    ))}
  </div>
);

export interface YhDateTimePickerProps {
  label?: string;
  value?: string; // YYYY-MM-DDTHH:mm
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export interface YhDatePickerProps {
  label?: string;
  /** YYYY-MM-DD */
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/** Single date picker (calendar only, displays dd/mm/yyyy). */
export const YhDatePicker: React.FC<YhDatePickerProps> = ({
  label = "Date",
  value = "",
  onChange,
  placeholder = "dd/mm/yyyy",
  className = "",
  disabled,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => parseDateKey(value), [value]);
  const [view, setView] = useState(() => {
    const base = selected || new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  useEffect(() => {
    if (!selected) return;
    setView(new Date(selected.getFullYear(), selected.getMonth(), 1));
  }, [selected]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, [open]);

  const display = selected ? formatDisplayDate(selected) : "";

  return (
    <div className={`relative w-full ${className}`} ref={rootRef}>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
      >
        <YhInput
          label={label}
          readOnly
          value={display}
          placeholder={placeholder}
          disabled={disabled}
          endAdornment={<CalendarIcon />}
          className="cursor-pointer"
        />
      </div>

      {open ? (
        <div className="absolute left-0 z-[1500] mt-1.5 overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.14)]">
          <YhCalendarGrid
            view={view}
            onViewChange={setView}
            selected={selected}
            onSelectDay={(d) => {
              onChange?.(toDateKey(d));
              setOpen(false);
            }}
            onClear={() => {
              onChange?.("");
              setOpen(false);
            }}
            onToday={() => {
              const t = new Date();
              setView(new Date(t.getFullYear(), t.getMonth(), 1));
              onChange?.(toDateKey(t));
              setOpen(false);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

/** Single date + time picker (calendar + hour/minute/AM-PM). */
export const YhDateTimePicker: React.FC<YhDateTimePickerProps> = ({
  label = "Date and Time",
  value = "",
  onChange,
  placeholder = "dd/mm/yyyy, --:-- --",
  className = "",
  disabled,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });

  const initial = useMemo(() => parseLocalDateTime(value) || new Date(), [value]);

  const [view, setView] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));
  const [selected, setSelected] = useState<Date>(initial);
  const [hour12, setHour12] = useState(() => {
    const h = initial.getHours() % 12 || 12;
    return pad2(h);
  });
  const [minute, setMinute] = useState(() => pad2(initial.getMinutes()));
  const [period, setPeriod] = useState<"AM" | "PM">(() =>
    initial.getHours() >= 12 ? "PM" : "AM"
  );

  useEffect(() => {
    const next = parseLocalDateTime(value);
    if (!next) return;
    setSelected(next);
    setView(new Date(next.getFullYear(), next.getMonth(), 1));
    const h = next.getHours() % 12 || 12;
    setHour12(pad2(h));
    setMinute(pad2(next.getMinutes()));
    setPeriod(next.getHours() >= 12 ? "PM" : "AM");
  }, [value]);

  const placePanel = () => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panel = panelRef.current;
    const margin = 8;
    const panelW = Math.min(
      panel?.offsetWidth || 480,
      Math.max(280, window.innerWidth - margin * 2)
    );
    const panelH = panel?.offsetHeight || 360;
    // Right-side drawers: grow the panel left from the input so time columns stay on screen.
    let left = rect.right - panelW;
    if (left < margin) left = margin;
    if (left + panelW > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - panelW - margin);
    }
    let top = rect.bottom + 6;
    if (top + panelH > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - panelH - 6);
    }
    setPanelPos((prev) =>
      prev.top === top && prev.left === left ? prev : { top, left }
    );
  };

  useLayoutEffect(() => {
    if (!open) return;
    placePanel();
    const frame = requestAnimationFrame(placePanel);
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onReflow = () => placePanel();
    document.addEventListener("mousedown", onDoc, true);
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      document.removeEventListener("mousedown", onDoc, true);
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open]);

  const emit = (day: Date, h12: string, min: string, peri: "AM" | "PM") => {
    let h = Number(h12) % 12;
    if (peri === "PM") h += 12;
    const next = new Date(day.getFullYear(), day.getMonth(), day.getDate(), h, Number(min), 0);
    onChange?.(
      `${toDateKey(next)}T${pad2(next.getHours())}:${pad2(next.getMinutes())}`
    );
  };

  const parsedValue = parseLocalDateTime(value);
  const display = parsedValue ? formatDisplayDateTime(parsedValue) : "";

  const hours = Array.from({ length: 12 }, (_, i) => pad2(i + 1));
  const minutes = Array.from({ length: 60 }, (_, i) => pad2(i));

  return (
    <div
      className={`relative w-full scroll-mb-24 ${className}`}
      ref={rootRef}
    >
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          rootRef.current?.scrollIntoView({
            block: "center",
            inline: "nearest",
          });
        }}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen((v) => !v);
            rootRef.current?.scrollIntoView({
              block: "center",
              inline: "nearest",
            });
          }
        }}
      >
        <YhInput
          label={label}
          readOnly
          value={display}
          placeholder={placeholder}
          disabled={disabled}
          endAdornment={<CalendarIcon />}
          className="cursor-pointer"
        />
      </div>

      {open
        ? createPortal(
            <div
              ref={panelRef}
              className="fixed z-[4000] flex w-max max-w-[calc(100vw-16px)] flex-row flex-nowrap max-h-[calc(100vh-16px)] overflow-auto rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.14)]"
              style={{ top: panelPos.top, left: panelPos.left }}
            >
              <YhCalendarGrid
                view={view}
                onViewChange={setView}
                selected={selected}
                onSelectDay={(d) => {
                  setSelected(d);
                  emit(d, hour12, minute, period);
                }}
                onClear={() => {
                  onChange?.("");
                  setOpen(false);
                }}
                onToday={() => {
                  const t = new Date();
                  setView(new Date(t.getFullYear(), t.getMonth(), 1));
                  setSelected(t);
                  const h = t.getHours() % 12 || 12;
                  const peri = t.getHours() >= 12 ? "PM" : "AM";
                  setHour12(pad2(h));
                  setMinute(pad2(t.getMinutes()));
                  setPeriod(peri);
                  emit(t, pad2(h), pad2(t.getMinutes()), peri);
                }}
              />
              <div className="flex shrink-0 flex-col border-l border-[#EEF0F5]">
                <div className="flex gap-1 p-2">
                  <TimeColumn
                    items={hours}
                    value={hour12}
                    onChange={(v) => {
                      setHour12(v);
                      emit(selected, v, minute, period);
                    }}
                  />
                  <TimeColumn
                    items={minutes}
                    value={minute}
                    onChange={(v) => {
                      setMinute(v);
                      emit(selected, hour12, v, period);
                    }}
                  />
                  <TimeColumn
                    items={["AM", "PM"]}
                    value={period}
                    onChange={(v) => {
                      const peri = v as "AM" | "PM";
                      setPeriod(peri);
                      emit(selected, hour12, minute, peri);
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="mx-2 mb-2 rounded-lg bg-[#841C39] px-2 py-1.5 text-[11px] font-semibold text-white"
                  onClick={() => {
                    emit(selected, hour12, minute, period);
                    setOpen(false);
                  }}
                >
                  Done
                </button>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export interface YhDateRangeValue {
  from: string | null; // DD-MM-YYYY
  to: string | null;
}

export interface YhDateRangePickerProps {
  label?: string;
  value?: YhDateRangeValue;
  onChange?: (value: YhDateRangeValue) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Optional quick shortcuts beside the calendar */
  showShortcuts?: boolean;
}

/** Reusable date-range picker (from → to) with optional shortcuts. */
export const YhDateRangePicker: React.FC<YhDateRangePickerProps> = ({
  label,
  value = { from: null, to: null },
  onChange,
  placeholder = "Date Range",
  className = "",
  disabled,
  showShortcuts = true,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const fromDate = parseDdMmYyyy(value.from);
  const toDate = parseDdMmYyyy(value.to);
  const [view, setView] = useState(
    () =>
      new Date(
        (fromDate || new Date()).getFullYear(),
        (fromDate || new Date()).getMonth(),
        1
      )
  );
  const [picking, setPicking] = useState<"from" | "to">("from");

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const place = () => {
      const el = buttonRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = showShortcuts ? 520 : 320;
      const height = 380;
      const gap = 8;
      let left = rect.left;
      if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
      if (left < 8) left = 8;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const top =
        spaceBelow >= 280
          ? rect.bottom + gap
          : Math.max(8, rect.top - gap - height);
      setMenuPos({ top, left });
    };
    place();
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc, true);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", onDoc, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, showShortcuts]);

  const display =
    value.from && value.to ? `${value.from} - ${value.to}` : "";

  const applyShortcut = (from: Date | null, to: Date | null) => {
    onChange?.({
      from: from ? formatDdMmYyyy(from) : null,
      to: to ? formatDdMmYyyy(to) : null,
    });
    setOpen(false);
    setPicking("from");
  };

  const shortcuts: Array<{ label: string; get: () => [Date | null, Date | null] }> = [
    {
      label: "This Week",
      get: () => {
        const t = new Date();
        const day = t.getDay();
        const start = new Date(t);
        start.setDate(t.getDate() - day);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        return [start, end];
      },
    },
    {
      label: "Last Week",
      get: () => {
        const t = new Date();
        const day = t.getDay();
        const end = new Date(t);
        end.setDate(t.getDate() - day - 1);
        const start = new Date(end);
        start.setDate(end.getDate() - 6);
        return [start, end];
      },
    },
    {
      label: "Current Month",
      get: () => {
        const t = new Date();
        return [
          new Date(t.getFullYear(), t.getMonth(), 1),
          new Date(t.getFullYear(), t.getMonth() + 1, 0),
        ];
      },
    },
    {
      label: "Last Month",
      get: () => {
        const t = new Date();
        return [
          new Date(t.getFullYear(), t.getMonth() - 1, 1),
          new Date(t.getFullYear(), t.getMonth(), 0),
        ];
      },
    },
    {
      label: "This Year",
      get: () => {
        const t = new Date();
        return [new Date(t.getFullYear(), 0, 1), t];
      },
    },
    {
      label: "Last Year",
      get: () => {
        const y = new Date().getFullYear() - 1;
        return [new Date(y, 0, 1), new Date(y, 11, 31)];
      },
    },
    { label: "Reset", get: () => [null, null] },
  ];

  const menu =
    open && menuPos
      ? createPortal(
          <div
            ref={menuRef}
            style={{ position: "fixed", top: menuPos.top, left: menuPos.left, zIndex: 4000 }}
            className="flex overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.14)]"
          >
          {showShortcuts ? (
            <div className="w-[140px] border-r border-[#EEF0F5] py-2">
              {shortcuts.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  className="block w-full px-3 py-2 text-left text-xs font-medium text-[#111827] hover:bg-[#F8FAFC] hover:text-[#A02448]"
                  onClick={() => {
                    const [a, b] = s.get();
                    applyShortcut(a, b);
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          ) : null}

          <div>
            <div className="flex gap-2 border-b border-[#EEF0F5] px-3 py-2 text-xs">
              <span
                className={`rounded-lg px-2 py-1 font-semibold ${
                  picking === "from"
                    ? "bg-[#F9F0F4] text-[#A02448]"
                    : "text-[#6B7280]"
                }`}
              >
                From: {value.from || "—"}
              </span>
              <span
                className={`rounded-lg px-2 py-1 font-semibold ${
                  picking === "to"
                    ? "bg-[#F9F0F4] text-[#A02448]"
                    : "text-[#6B7280]"
                }`}
              >
                To: {value.to || "—"}
              </span>
            </div>
            <YhCalendarGrid
              view={view}
              onViewChange={setView}
              rangeStart={fromDate}
              rangeEnd={toDate}
              selected={picking === "from" ? fromDate : toDate}
              onSelectDay={(d) => {
                if (picking === "from") {
                  const day = formatDdMmYyyy(d);
                  onChange?.({ from: day, to: day });
                  setPicking("to");
                } else {
                  let from = value.from;
                  let to = formatDdMmYyyy(d);
                  const fromD = parseDdMmYyyy(from);
                  if (fromD && d < fromD) {
                    to = from!;
                    from = formatDdMmYyyy(d);
                  }
                  onChange?.({ from, to });
                  setOpen(false);
                  setPicking("from");
                }
              }}
              onClear={() => applyShortcut(null, null)}
              onToday={() => {
                const t = new Date();
                applyShortcut(t, t);
              }}
            />
          </div>
        </div>,
          document.body
        )
      : null;

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`inline-flex h-10 items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3.5 text-sm font-medium text-[#374151] transition hover:border-[#D4B8C0] hover:bg-[#F8FAFC] disabled:opacity-60 ${
          className.includes("w-full") ? "w-full" : "shrink-0"
        }`}
      >
        <CalendarIcon />
        <span className="truncate">{display || placeholder}</span>
      </button>
      {menu}
      {label ? (
        <span className="sr-only">{label}</span>
      ) : null}
    </div>
  );
};

export default YhDateTimePicker;
